import * as React from 'react';
import { Link, NavLink, Outlet, useLocation, useParams } from 'react-router-dom';
import { MonitorIcon, SmartphoneIcon } from 'lucide-react';

import { appIdentity } from '@/app/app-config';
import { Button } from '@/components/ui/button';
import { PageErrorBoundary } from '@/components/layout/page-error-boundary';
import { tenantPath } from '@/features/tenancy/urls';
import { useAppRoutes } from '@/hooks/use-app-routes';
import { createMobileNavigation } from '@/lib/mobile-navigation';
import { cn } from '@/lib/utils';

function tabTitle(pathname: string): string {
  if (pathname.includes('/today/assistant')) return 'Agent';
  if (pathname.includes('/today/book')) return 'Book';
  if (pathname.includes('/today/scan')) return 'Scan';
  if (pathname.includes('/today/map')) return 'Map';
  return 'Today';
}

/** Strip trailing slash for stable matching. */
function normalizePath(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed.length > 0 ? trimmed : '/';
}

/**
 * Phone-first chrome for the mobile experience: compact header, safe-area
 * insets, and a bottom tab bar. Desktop sidebar/top-nav are intentionally
 * absent — AppShell routes mobile `/today` paths here without chrome.
 */
export function MobileShell() {
  const location = useLocation();
  const routes = useAppRoutes();
  const { tenantSlug } = useParams<{ tenantSlug?: string }>();
  const title = tabTitle(location.pathname);
  const desktopHome = tenantSlug ? tenantPath(tenantSlug, '/') : '/';
  const tabs = createMobileNavigation(routes);

  React.useEffect(() => {
    document.title = `${title} · ${appIdentity.name}`;
    return () => {
      document.title = appIdentity.name;
    };
  }, [title]);

  return (
    <div
      className="bg-background text-foreground flex h-svh flex-col overflow-hidden"
      data-mobile-shell="true"
    >
      <header
        className="border-border/80 bg-background/90 supports-[backdrop-filter]:bg-background/75 z-30 shrink-0 border-b backdrop-blur-xl"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <a
          href="#main-content"
          className="bg-background text-foreground ring-ring sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:px-3 focus:py-2 focus:text-sm focus:ring-2"
        >
          Skip to main content
        </a>
        <div className="mx-auto flex w-full max-w-lg items-center justify-between gap-3 px-4 pb-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="bg-primary text-primary-foreground flex size-9 shrink-0 items-center justify-center rounded-2xl shadow-sm">
              <SmartphoneIcon className="size-4" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
                {appIdentity.name}
              </p>
              <h1 className="truncate text-base font-semibold tracking-tight">{title}</h1>
            </div>
          </div>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground h-9 shrink-0 gap-1.5 rounded-full px-3"
          >
            <Link to={desktopHome} aria-label="Switch to desktop experience">
              <MonitorIcon className="size-3.5" aria-hidden />
              <span className="text-xs font-medium">Desktop</span>
            </Link>
          </Button>
        </div>
      </header>

      <main
        id="main-content"
        className="mx-auto w-full max-w-lg flex-1 overflow-y-auto overscroll-y-contain px-4 pt-4"
        style={{
          paddingBottom: 'calc(5.5rem + env(safe-area-inset-bottom, 0px))'
        }}
      >
        <PageErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </PageErrorBoundary>
      </main>

      <nav
        aria-label="Mobile"
        className="border-border/80 bg-background/95 supports-[backdrop-filter]:bg-background/80 fixed inset-x-0 bottom-0 z-40 border-t backdrop-blur-xl"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
      >
        <div className={cn('mx-auto grid w-full max-w-lg gap-1 px-2 pt-1.5', tabs.length === 5 ? 'grid-cols-5' : tabs.length === 4 ? 'grid-cols-4' : tabs.length === 3 ? 'grid-cols-3' : 'grid-cols-2')}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-14 flex-col items-center justify-center gap-0.5 rounded-2xl px-1 py-1.5 text-[11px] font-medium transition-colors',
                    'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
                    isActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={cn(
                        'flex size-8 items-center justify-center rounded-xl transition-colors',
                        isActive ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-transparent'
                      )}
                    >
                      <Icon className="size-4" aria-hidden />
                    </span>
                    <span>{tab.title}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

/**
 * True when the current path belongs to the mobile experience.
 * Supports bare `/today` and tenant-scoped `/t/:slug/today` paths.
 */
export function isMobileExperiencePath(pathname: string): boolean {
  const path = normalizePath(pathname);
  return path === '/today' || path.endsWith('/today') || path.includes('/today/');
}
